import type { LoaderNormal } from './loaderNormal.js';
import { JsonUtility } from '../../app/jsonUtility.js';
import { CommonUtil } from '../../common/commonUtil.js';
import { JsonObject, JsonValue } from '../../app/json/jsonValue.js';
import type { DynamicResults } from '../../model/dynamicResults.js';
import type { InheritanceOperation } from '../../model/inheritanceOperation.js';
import type { NavigationDecision } from '../../model/navigationDecision.js';
import type { AppFileResult } from '../../config/iConfig.js';
import { DynamicConfigMap } from '../../registry/dynamicConfigMap.js';
import type { ITransientStore } from '../../registry/iface/iTransientStore.js';

const configAccessorMethods = {
    defaultReplaceTemplateName(this: LoaderNormal): string { return this._config!.defaultReplaceTemplateName; },
    defaultSkipTemplateName(this: LoaderNormal): string { return this._config!.defaultSkipTemplateName; },
    defaultReplaceHtml(this: LoaderNormal): string { return this._config!.defaultReplaceHtml; },
    defaultSkipHtml(this: LoaderNormal): string { return this._config!.defaultSkipHtml; },
    defaultSkipJson(this: LoaderNormal): string { return this._config!.defaultSkipJson; },
    defaultAppViewPrefix(this: LoaderNormal): string { return this._config!.defaultAppViewPrefix; },
    maxArrayOperationItems(this: LoaderNormal): number { return this._config!.maxArrayOperationItems; },
    maxRecursionDepth(this: LoaderNormal): number { return this._config!.maxRecursionDepth; },
    maxMergeIterations(this: LoaderNormal): number { return this._config!.maxMergeIterations; }
};

export const iloaderMethods = {
    getBaseComponentName(this: LoaderNormal, placeholderOrSlotholderName: string, currentTemplateName: string): { baseComponentName: string; isReuseComponent: boolean } {
        const { baseTemplateName, isReusePattern } = CommonUtil.parseComponentReusePattern(placeholderOrSlotholderName, currentTemplateName);
        return { baseComponentName: baseTemplateName, isReuseComponent: isReusePattern };
    },

    getDynamicDataConfig(this: LoaderNormal, appSite: string): DynamicConfigMap | null {
        return this._dynamicDataConfigStore[appSite] || null;
    },

    setDynamicResults(this: LoaderNormal, dynamicResults: DynamicResults): void {
        this._dynamicResults = dynamicResults;
    },

    getDynamicDataTiming(this: LoaderNormal): { nanos: number; count: number } {
        const result = { nanos: this._dynamicDataNanos, count: this._dynamicDataCount };
        this._dynamicDataNanos = 0;
        this._dynamicDataCount = 0;
        return result;
    },

    getDynamicViewTiming(this: LoaderNormal): { nanos: number; count: number } {
        const result = { nanos: this._dynamicViewNanos, count: this._dynamicViewCount };
        this._dynamicViewNanos = 0;
        this._dynamicViewCount = 0;
        return result;
    },

    getDynamicActionConfig(this: LoaderNormal, appSite: string): DynamicConfigMap | null {
        return this._dynamicActionConfigStore[appSite] || null;
    },

    getDynamicDataParams(this: LoaderNormal, appSite: string, paramFile: string): JsonObject | null {
        const { dynamicParams, storeKey } = this.resolveDynamicParamSource(this._dynamicDataParams, appSite, paramFile);
        return this.getDynamicParamByKey(dynamicParams, storeKey, this._dynamicDataParamsStore);
    },

    // #region DynamicClientData

    getDynamicClientDataConfig(this: LoaderNormal, appSite: string): DynamicConfigMap | null {
        return this._dynamicClientDataConfigStore[appSite] || null;
    },

    getDynamicClientDataParams(this: LoaderNormal, appSite: string, paramFile: string): JsonObject | null {
        const { dynamicParams, storeKey } = this.resolveDynamicParamSource(null, appSite, paramFile);
        return this.getDynamicParamByKey(dynamicParams, storeKey, this._dynamicClientDataParamsStore);
    },

    // #endregion

    getDynamicViewConfig(this: LoaderNormal, appSite: string): DynamicConfigMap | null {
        return this._dynamicViewConfigStore[appSite] || null;
    },

    getDynamicViewParams(this: LoaderNormal, appSite: string, paramFile: string): JsonObject | null {
        const { dynamicParams, storeKey } = this.resolveDynamicParamSource(this._dynamicViewParams, appSite, paramFile);
        return this.getDynamicParamByKey(dynamicParams, storeKey, this._dynamicViewParamsStore);
    },

    getDynamicActionParams(this: LoaderNormal, appSite: string, paramFile: string): JsonObject | null {
        const { dynamicParams, storeKey } = this.resolveDynamicParamSource(this._dynamicActionParams, appSite, paramFile);
        return this.getDynamicParamByKey(dynamicParams, storeKey, this._dynamicActionParamsStore);
    },

    getDynamicAppSiteConfig(this: LoaderNormal, appSite: string): DynamicConfigMap | null {
        return this._dynamicAppSiteConfigStore[appSite] || null;
    },

    getDynamicAppSiteParams(this: LoaderNormal, appSite: string, paramFile: string): JsonObject | null {
        const { dynamicParams, storeKey } = this.resolveDynamicParamSource(this._dynamicAppSiteParams, appSite, paramFile);
        return this.getDynamicParamByKey(dynamicParams, storeKey, this._dynamicAppSiteParamsStore);
    },

    getDynamicParamByKey(this: LoaderNormal, dynamicParams: JsonObject | null, storeKey: string, paramsStore: JsonObject): JsonObject | null {
        if (dynamicParams) {
            if (dynamicParams.has(storeKey)) {
                const value = dynamicParams.get(storeKey);
                return value && value.isObject ? value.asObject() : null;
            }
            return null;
        }

        const storeValue = paramsStore.get(storeKey);
        return storeValue.isObject ? storeValue.asObject() : null;
    },

    resolveDynamicParamSource(this: LoaderNormal, clientParams: JsonObject | null, appSite: string, paramFile: string): { dynamicParams: JsonObject | null; storeKey: string } {
        if (clientParams) {
            return { dynamicParams: clientParams, storeKey: paramFile };
        }

        return { dynamicParams: null, storeKey: `${appSite}:${paramFile}` };
    },

    getGlobalData(this: LoaderNormal, appSite: string): JsonObject | null {
        const result = this._globalDataStore.get(appSite);
        return result.isObject ? result.getObject() : null;
    },

    getGlobalViewData(this: LoaderNormal, appSite: string): JsonObject | null {
        const result = this._globalViewDataStore.get(appSite);
        return result.isObject ? result.getObject() : null;
    },

    getGlobalViewDataForComponent(this: LoaderNormal, appSite: string, componentName: string): JsonObject | null {
        const result = this._globalViewDataComponentStore.get(`${appSite}:${componentName.toLowerCase()}`);
        return result.isObject ? result.getObject() : null;
    },

    getDynamicActionTiming(this: LoaderNormal): { nanos: number; count: number } {
        const result = { nanos: this._dynamicActionNanos, count: this._dynamicActionCount };
        this._dynamicActionNanos = 0;
        this._dynamicActionCount = 0;
        return result;
    },

    getDynamicClientDataTiming(this: LoaderNormal): { nanos: number; count: number } {
        const result = { nanos: this._dynamicClientDataNanos, count: this._dynamicClientDataCount };
        this._dynamicClientDataNanos = 0;
        this._dynamicClientDataCount = 0;
        return result;
    },

    setActionMode(this: LoaderNormal, actionMode: boolean): void {
        this._actionMode = actionMode;
    },

    getActionMode(this: LoaderNormal): boolean {
        return !!this._actionMode;
    },

    getAndClearInheritanceOperations(this: LoaderNormal): InheritanceOperation[] {
        const ops = this._inheritanceOperations;
        this._inheritanceOperations = [];
        return ops;
    },

    getAndClearNavigationTrace(this: LoaderNormal): NavigationDecision[] {
        const trace = this._lastNavigationTrace;
        this._lastNavigationTrace = [];
        return trace;
    },

    getAppFile(this: LoaderNormal, appSite: string, appView: string): AppFileResult {
        return this._config!.getAppFile(appSite, appView);
    },

    getTemplatesApiResponse(this: LoaderNormal, appSite: string, appView: string, contextJson: JsonObject, serverTimeMs: number, includeSearchAppSites: boolean = true, maskHtml: boolean = false, orderedTemplateNames: string[] | null = null): string {
        const appFileResult = this._config!.getAppFile(appSite, appView);
        const appFile = appFileResult.appFile;
        const templatesDict = this._getAllTemplatesJson(includeSearchAppSites, orderedTemplateNames);
        const config = this._config!;

        const sb: string[] = [];
        sb.push('{"Templates":{');
        let first = true;
        for (const key in templatesDict) {
            if (!first) sb.push(',');
            first = false;
            sb.push('"');
            sb.push((this.constructor as typeof LoaderNormal)._escapeJsonString(key));
            sb.push('":');
            sb.push(templatesDict[key].toJson());
        }
        sb.push('}');
        sb.push(',"AppSite":"');
        sb.push((this.constructor as typeof LoaderNormal)._escapeJsonString(appSite));
        sb.push('","AppFile":"');
        sb.push((this.constructor as typeof LoaderNormal)._escapeJsonString(appFile));
        sb.push('","AppView":"');
        sb.push((this.constructor as typeof LoaderNormal)._escapeJsonString(appView));
        sb.push('","SearchAppSites":"');
        sb.push((this.constructor as typeof LoaderNormal)._escapeJsonString(this.getSearchAppSitesForAppSite(appSite)));
        sb.push('","Config":{');
        sb.push('"defaultReplaceHtml":"');
        sb.push((this.constructor as typeof LoaderNormal)._escapeJsonString(config.defaultReplaceHtml));
        sb.push('","defaultSkipHtml":"');
        sb.push((this.constructor as typeof LoaderNormal)._escapeJsonString(config.defaultSkipHtml));
        sb.push('","defaultSkipJson":"');
        sb.push((this.constructor as typeof LoaderNormal)._escapeJsonString(config.defaultSkipJson));
        sb.push('","defaultReplaceTemplateName":"');
        sb.push((this.constructor as typeof LoaderNormal)._escapeJsonString(config.defaultReplaceTemplateName));
        sb.push('","defaultSkipTemplateName":"');
        sb.push((this.constructor as typeof LoaderNormal)._escapeJsonString(config.defaultSkipTemplateName));
        sb.push('","defaultAppViewPrefix":"');
        sb.push((this.constructor as typeof LoaderNormal)._escapeJsonString(config.defaultAppViewPrefix));
        sb.push('","maxRecursionDepth":');
        sb.push(config.maxRecursionDepth.toString());
        sb.push(',"maxArrayOperationItems":');
        sb.push(config.maxArrayOperationItems.toString());
        sb.push(',"dynamicDataUseResolvedAppSite":');
        sb.push(config.dynamicDataUseResolvedAppSite ? 'true' : 'false');
        sb.push('}');
        sb.push(',"ContextJson":');
        sb.push(JsonUtility.serializeObject(contextJson));

        this._serializePerSiteStores(sb, appSite);
        sb.push(',"ServerTimeMs":');
        sb.push(serverTimeMs.toString());
        sb.push('}');
        return sb.join('');
    },

    setTransientStore(this: LoaderNormal, transientStore: ITransientStore): void {
        this._transientStore = transientStore;
    },

    getTransientStore(this: LoaderNormal): ITransientStore {
        return this._transientStore;
    },

    _serializePerSiteStores(this: LoaderNormal, sb: string[], appSite: string): void {
        const allSites = [appSite];
        const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
        if (searchAppSites) {
            for (const s of searchAppSites.split(',')) {
                const trimmed = s.trim();
                if (trimmed && !allSites.includes(trimmed)) allSites.push(trimmed);
            }
        }
        // Also cover any other loaded AppSite (e.g. a dynamic-AppSite redirect target that Templates
        // carries) so its dynamic stores ship too; otherwise the client re-resolves to that AppSite but
        // lacks its DynamicData/View config and renders a partial result. Sorted for byte-stable output.
        const extraSites: string[] = [];
        for (const site of this._appSiteCache.keys()) {
            if (!allSites.includes(site)) extraSites.push(site);
        }
        extraSites.sort((a, b) => { const la = a.toLowerCase(), lb = b.toLowerCase(); return la < lb ? -1 : la > lb ? 1 : 0; });
        for (const site of extraSites) allSites.push(site);
        sb.push(',"PerSiteStores":{');
        sb.push('"GlobalData":{');
        for (let i = 0; i < allSites.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"');
            sb.push((this.constructor as typeof LoaderNormal)._escapeJsonString(allSites[i]));
            sb.push('":');
            const gd = this.getGlobalData(allSites[i]);
            sb.push(gd ? JsonUtility.serializeObject(gd) : 'null');
        }
        sb.push('},');
        sb.push('"GlobalViewData":{');
        for (let i = 0; i < allSites.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"');
            sb.push((this.constructor as typeof LoaderNormal)._escapeJsonString(allSites[i]));
            sb.push('":');
            const gvd = this.getGlobalViewData(allSites[i]);
            if (gvd) {
                const resolved = new JsonObject();
                for (const componentName of gvd.keys()) {
                    const resolvedData = this.getGlobalViewDataForComponent(allSites[i], componentName);
                    if (resolvedData) resolved.set(componentName, JsonValue.object(resolvedData));
                }
                sb.push(resolved.size > 0 ? JsonUtility.serializeObject(resolved) : 'null');
            } else {
                sb.push('null');
            }
        }
        sb.push('},');
        // The client re-merge ships the config of every handler tier that EXECUTES on the client: DynamicView,
        // and the PURE client bindings below. Server-only Data/Action/AppSite handlers — and the Embedded
        // client-data PREPARER — stay unshipped; their results are already baked into the shipped templates
        // or arrive as the delivered island.
        this._serializePerSiteDynamicConfig(sb, allSites, 'DynamicViewConfig', (site: string) => this.getDynamicViewConfig(site));
        sb.push(',');
        this._serializePerSiteParamsFromStore(sb, allSites, 'DynamicViewParams', this._dynamicViewParamsStore);
        sb.push(',');
        // PURE client bindings + the params they name. A pure handler resolves its params from the BINDING
        // (tryResolvePureClientHandlerParams), so without these the reconstruction sources silently skip it
        // and the replay diverges from the server render.
        this._serializePerSiteDynamicConfig(sb, allSites, 'DynamicClientConfig', (site: string) => CommonUtil.filterPureClientBindings(this.getDynamicClientDataConfig(site)));
        sb.push(',');
        this._serializePerSiteParamsFromStore(sb, allSites, 'DynamicClientParams',
            CommonUtil.filterPureClientParams(this._dynamicClientDataParamsStore, allSites, (site: string) => this.getDynamicClientDataConfig(site)));
        sb.push('}');
    },

    _serializeDynamicConfigToJson(this: LoaderNormal, config: DynamicConfigMap): string {
        return DynamicConfigMap.serializeDynamicConfigToJson(config);
    },

    _serializePerSiteDynamicConfig(this: LoaderNormal, sb: string[], allSites: string[], key: string, getter: (site: string) => DynamicConfigMap | null): void {
        sb.push('"');
        sb.push(key);
        sb.push('":{');
        for (let i = 0; i < allSites.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"');
            sb.push((this.constructor as typeof LoaderNormal)._escapeJsonString(allSites[i]));
            sb.push('":');
            const config = getter(allSites[i]);
            sb.push(config ? DynamicConfigMap.serializeDynamicConfigToJson(config) : 'null');
        }
        sb.push('}');
    },

    _serializePerSiteParamsFromStore(this: LoaderNormal, sb: string[], allSites: string[], key: string, paramsStore: JsonObject): void {
        sb.push('"');
        sb.push(key);
        sb.push('":{');
        let firstSite = true;
        for (let i = 0; i < allSites.length; i++) {
            const site = allSites[i];
            const prefix = `${site}:`;
            const siteParams: Record<string, JsonObject> = {};
            for (const storeKey of paramsStore.keys()) {
                if (storeKey.toLowerCase().startsWith(prefix.toLowerCase())) {
                    const storeValue = paramsStore.get(storeKey);
                    if (storeValue.isObject) {
                        siteParams[storeKey.substring(prefix.length)] = storeValue.getObject();
                    }
                }
            }
            if (Object.keys(siteParams).length === 0) continue;
            if (!firstSite) sb.push(',');
            firstSite = false;
            sb.push('"');
            sb.push((this.constructor as typeof LoaderNormal)._escapeJsonString(site));
            sb.push('":{');
            let firstParam = true;
            for (const paramKey of Object.keys(siteParams)) {
                if (!firstParam) sb.push(',');
                firstParam = false;
                sb.push('"');
                sb.push((this.constructor as typeof LoaderNormal)._escapeJsonString(paramKey));
                sb.push('":');
                sb.push(JsonUtility.serializeObject(siteParams[paramKey]));
            }
            sb.push('}');
        }
        sb.push('}');
    },

    getComponentId(this: LoaderNormal, appSite: string, componentName: string): string | null {
        let dynamicConfig = this.getDynamicViewConfig(appSite);
        let configAppSite: string | null = dynamicConfig ? appSite : null;

        if (!dynamicConfig) {
            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites) {
                for (const search of searchAppSites.split(',')) {
                    const trimmed = search.trim();
                    if (!trimmed) continue;
                    dynamicConfig = this.getDynamicViewConfig(trimmed);
                    if (dynamicConfig) {
                        configAppSite = trimmed;
                        break;
                    }
                }
            }
        }

        if (!dynamicConfig) return null;

        let matchedKey: string | null = null;
        const componentNameLower = componentName.toLowerCase();
        for (const key of dynamicConfig.keys()) {
            if (key !== '*' && key.toLowerCase() === componentNameLower) {
                matchedKey = key;
                break;
            }
        }
        if (matchedKey === null && dynamicConfig.has('*')) matchedKey = '*';

        if (matchedKey === null) {
            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites) {
                for (const search of searchAppSites.split(',')) {
                    const trimmed = search.trim();
                    if (!trimmed) continue;
                    const searchConfig = this.getDynamicViewConfig(trimmed);
                    if (!searchConfig) continue;

                    for (const key of searchConfig.keys()) {
                        if (key !== '*' && key.toLowerCase() === componentNameLower) {
                            matchedKey = key;
                            dynamicConfig = searchConfig;
                            configAppSite = trimmed;
                            break;
                        }
                    }
                    if (matchedKey !== null) break;

                    if (searchConfig.has('*')) {
                        matchedKey = '*';
                        dynamicConfig = searchConfig;
                        configAppSite = trimmed;
                        break;
                    }
                }
            }
        }

        if (matchedKey === null) {
            const configsToSearch: Array<{ config: DynamicConfigMap; appSite: string }> = [];
            configsToSearch.push({ config: dynamicConfig, appSite: configAppSite || '' });

            const searchAppSites = this.getSearchAppSitesForAppSite(appSite);
            if (searchAppSites) {
                for (const search of searchAppSites.split(',')) {
                    const trimmed = search.trim();
                    if (!trimmed) continue;
                    const searchConfig = this.getDynamicViewConfig(trimmed);
                    if (searchConfig && !configsToSearch.some(entry => entry.appSite === trimmed)) {
                        configsToSearch.push({ config: searchConfig, appSite: trimmed });
                    }
                }
            }

            for (const entry of configsToSearch) {
                for (const [, handlers] of entry.config) {
                    for (const handler of handlers) {
                        if (handler.iType.toLowerCase() !== 'baseview' || !handler.iParamFile) continue;
                        const paramJson = this.getDynamicViewParams(entry.appSite, handler.iParamFile);
                        if (!paramJson || !paramJson.has('ComponentID')) continue;
                        const componentIdValue = paramJson.get('ComponentID');
                        const componentId = componentIdValue.isString
                            ? componentIdValue.asString()
                            : componentIdValue.toString();
                        if (componentId?.toLowerCase() === componentNameLower) return componentId;
                    }
                }
            }
            return null;
        }

        const handlersValue = dynamicConfig.get(matchedKey);
        if (!Array.isArray(handlersValue)) return null;

        let baseViewParamFile: string | null = null;
        for (const handlerObj of handlersValue) {
            if (!handlerObj || typeof handlerObj !== 'object') continue;
            const iViewType = handlerObj.iType;
            if (!iViewType) continue;
            if (iViewType.toLowerCase() === 'baseview') {
                if (handlerObj.iParamFile) baseViewParamFile = handlerObj.iParamFile;
                break;
            }
        }

        if (baseViewParamFile === null) return null;

        const paramJson = this.getDynamicViewParams(configAppSite!, baseViewParamFile);
        if (paramJson) {
            const componentIdValue = paramJson.get('ComponentID');
            if (componentIdValue && componentIdValue.isString) {
                const componentId = componentIdValue.asString()!;
                if (componentId) return componentId;
            }
        }

        // Default to camelCase of componentName. NOT a guess: BaseView.getComponentId — the renderer that
        // actually emits the wrapper — resolves the id the same way (paramJson -> componentJson -> camelCase),
        // so an omitted ComponentID is a DECLARED default, not an absent value. This method's whole job is to
        // answer "what id did the renderer emit for this component", so it has to mirror that chain: throwing
        // here made /api/get-components 500 for every BaseView component whose param file omits ComponentID
        // (621 of the 1505 shipped param files, including every AppSites/Main component on every surface),
        // while the renderer went on emitting id="mainHeader". LoaderPreProcess.getComponentId mirrors it too.
        if (!componentName) return componentName;
        return componentName.charAt(0).toLowerCase() + componentName.substring(1);
    }
};

export const iloaderStaticMethods = {};

export const configPropertyDescriptors: PropertyDescriptorMap = {
    DefaultReplaceTemplateName: {
        get: configAccessorMethods.defaultReplaceTemplateName,
        configurable: true
    },
    defaultReplaceTemplateName: {
        get: configAccessorMethods.defaultReplaceTemplateName,
        configurable: true
    },
    DefaultSkipTemplateName: {
        get: configAccessorMethods.defaultSkipTemplateName,
        configurable: true
    },
    defaultSkipTemplateName: {
        get: configAccessorMethods.defaultSkipTemplateName,
        configurable: true
    },
    DefaultReplaceHtml: {
        get: configAccessorMethods.defaultReplaceHtml,
        configurable: true
    },
    defaultReplaceHtml: {
        get: configAccessorMethods.defaultReplaceHtml,
        configurable: true
    },
    DefaultSkipHtml: {
        get: configAccessorMethods.defaultSkipHtml,
        configurable: true
    },
    defaultSkipHtml: {
        get: configAccessorMethods.defaultSkipHtml,
        configurable: true
    },
    DefaultSkipJson: {
        get: configAccessorMethods.defaultSkipJson,
        configurable: true
    },
    defaultSkipJson: {
        get: configAccessorMethods.defaultSkipJson,
        configurable: true
    },
    DefaultAppViewPrefix: {
        get: configAccessorMethods.defaultAppViewPrefix,
        configurable: true
    },
    defaultAppViewPrefix: {
        get: configAccessorMethods.defaultAppViewPrefix,
        configurable: true
    },
    MaxArrayOperationItems: {
        get: configAccessorMethods.maxArrayOperationItems,
        configurable: true
    },
    maxArrayOperationItems: {
        get: configAccessorMethods.maxArrayOperationItems,
        configurable: true
    },
    MaxRecursionDepth: {
        get: configAccessorMethods.maxRecursionDepth,
        configurable: true
    },
    maxRecursionDepth: {
        get: configAccessorMethods.maxRecursionDepth,
        configurable: true
    },
    maxMergeIterations: {
        get: configAccessorMethods.maxMergeIterations,
        configurable: true
    }
};
